import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import "./PartnerProfile.css";
import { getPartnerProfile } from "./partnerApi";

const TYPES = { hotel: "🏨 Hotel", cottage: "🏡 Cottage", vehicle: "🚗 Vehicle", homestay: "🏠 Homestay", resort: "🌴 Resort", other: "📦" };
const inr = (n) => "₹" + Number(n || 0).toLocaleString("en-IN");

export default function PartnerProfile() {
  const { slug } = useParams();
  const [p, setP] = useState(undefined); // undefined=loading, null=not found
  const [lightbox, setLightbox] = useState(null);
  const [menuOpen, setMenuOpen] = useState(false);

  useEffect(() => {
    getPartnerProfile(slug).then(setP).catch(() => setP(null));
  }, [slug]);

  // Use the business name as the browser tab title — this is the partner's own site
  useEffect(() => {
    if (p && p !== null) {
      const prev = document.title;
      document.title = p.businessName || p.name || "Profile";
      return () => { document.title = prev; };
    }
  }, [p]);

  const share = async () => {
    const url = window.location.href;
    if (navigator.share) { try { await navigator.share({ title: p.businessName, url }); return; } catch {} }
    navigator.clipboard?.writeText(url);
    alert("Link copied!");
  };

  if (p === undefined) return <div className="pp-state">Loading…</div>;
  if (p === null) return <div className="pp-state">This page is not available.</div>;

  const wa = (p.phone || "").replace(/\D/g, "");
  const waHref = wa ? `https://wa.me/91${wa}?text=${encodeURIComponent(`Hi ${p.businessName || p.name}, I found your page online!`)}` : null;
  const initials = (p.businessName || p.name || "?").slice(0, 2).toUpperCase();
  const logoSrc = p.gallery?.[0] || p.coverPhoto || "";

  const navLinks = [
    p.amenities?.length > 0 && ["#amenities", "Amenities"],
    p.ads?.length > 0 && ["#listings", "Listings"],
    p.gallery?.length > 0 && ["#gallery", "Gallery"],
    p.testimonials?.length > 0 && ["#reviews", "Reviews"],
    ["#contact", "Contact"],
  ].filter(Boolean);

  const go = (e, href) => { e.preventDefault(); setMenuOpen(false); document.querySelector(href)?.scrollIntoView({ behavior: "smooth" }); };

  return (
    <div className="pp-site" id="top">
      {/* ───── Own branded header ───── */}
      <header className="pp-nav">
        <div className="pp-nav-inner">
          <a href="#top" className="pp-nav-brand" onClick={(e) => go(e, "#top")}>
            {logoSrc ? <img src={logoSrc} alt="" /> : <span className="pp-nav-logo">{initials}</span>}
            <span className="pp-nav-name">{p.businessName || p.name}</span>
          </a>
          <nav className={`pp-nav-links ${menuOpen ? "open" : ""}`}>
            {navLinks.map(([href, label]) => (
              <a key={href} href={href} onClick={(e) => go(e, href)}>{label}</a>
            ))}
            {waHref && <a className="pp-nav-cta" href={waHref} target="_blank" rel="noopener noreferrer">💬 Enquire</a>}
          </nav>
          <button className="pp-nav-toggle" onClick={() => setMenuOpen(!menuOpen)} aria-label="Menu">{menuOpen ? "✕" : "☰"}</button>
        </div>
      </header>

      {/* ───── Hero / cover ───── */}
      <div className="pp-cover" style={p.coverPhoto ? { backgroundImage: `url(${p.coverPhoto})` } : {}}>
        <div className="pp-cover-shade" />
      </div>
      <div className="pp-header">
        <div className="pp-avatar">{logoSrc ? <img src={logoSrc} alt="" /> : <span>{initials}</span>}</div>
        <div className="pp-id">
          <h1>{p.businessName || p.name}</h1>
          <p className="pp-type">{TYPES[p.businessType] || p.businessType}</p>
          {p.bio && <p className="pp-bio">{p.bio}</p>}
          <div className="pp-socials">
            {p.socials?.instagram && <a href={p.socials.instagram} target="_blank" rel="noopener noreferrer">📸 Instagram</a>}
            {p.socials?.facebook && <a href={p.socials.facebook} target="_blank" rel="noopener noreferrer">📘 Facebook</a>}
            {p.socials?.website && <a href={p.socials.website} target="_blank" rel="noopener noreferrer">🌐 Website</a>}
          </div>
        </div>
        <div className="pp-actions">
          {waHref && <a className="pp-btn wa" href={waHref} target="_blank" rel="noopener noreferrer">💬 WhatsApp</a>}
          {p.phone && <a className="pp-btn call" href={`tel:${p.phone}`}>📞 Call</a>}
          <button className="pp-btn share" onClick={share}>🔗 Share</button>
        </div>
      </div>

      {/* Amenities */}
      {p.amenities?.length > 0 && (
        <section className="pp-section" id="amenities">
          <h2>✨ Amenities &amp; Features</h2>
          <ul className="pp-amenities">{p.amenities.map((a, i) => <li key={i}>✓ {a}</li>)}</ul>
        </section>
      )}

      {/* Listings */}
      {p.ads?.length > 0 && (
        <section className="pp-section" id="listings">
          <h2>🏷️ Listings</h2>
          <div className="pp-listings">
            {p.ads.map((ad) => (
              <div className="pp-listing" key={ad.id}>
                {ad.photos?.[0] ? <img src={ad.photos[0]} alt={ad.title} /> : <div className="pp-noimg">No photo</div>}
                <div className="pp-listing-body">
                  <h3>{ad.title}</h3>
                  {ad.location && <p>📍 {ad.location}</p>}
                  {ad.priceFrom > 0 && <p className="pp-price">{inr(ad.priceFrom)} <span>{ad.priceUnit}</span></p>}
                </div>
              </div>
            ))}
          </div>
        </section>
      )}

      {/* Photo gallery */}
      {p.gallery?.length > 0 && (
        <section className="pp-section" id="gallery">
          <h2>📷 Photo Gallery</h2>
          <div className="pp-gallery">
            {p.gallery.map((url, i) => (
              <button className="pp-gphoto" key={i} onClick={() => setLightbox(url)}>
                <img src={url} alt="" loading="lazy" />
              </button>
            ))}
          </div>
        </section>
      )}

      {/* Videos */}
      {p.videos?.length > 0 && (
        <section className="pp-section" id="videos">
          <h2>🎬 Videos</h2>
          <div className="pp-videos">
            {p.videos.map((url, i) => <video key={i} src={url} controls className="pp-video" />)}
          </div>
        </section>
      )}

      {/* Testimonials */}
      {p.testimonials?.length > 0 && (
        <section className="pp-section" id="reviews">
          <h2>⭐ Reviews &amp; Testimonials</h2>
          <div className="pp-reviews">
            {p.testimonials.map((t, i) => (
              <div className="pp-review" key={i}>
                <div className="pp-stars">{"★".repeat(t.rating || 5)}<span>{"★".repeat(5 - (t.rating || 5))}</span></div>
                <p className="pp-review-text">“{t.text}”</p>
                <p className="pp-review-name">— {t.name}</p>
              </div>
            ))}
          </div>
        </section>
      )}

      {/* Location & Contact */}
      {(p.address || p.mapLink || p.phone || p.contactEmail || p.contactPerson) && (
        <section className="pp-section" id="contact">
          <h2>📍 Location &amp; Contact</h2>
          <div className="pp-contact-grid">
            <div className="pp-contact-info">
              {p.address && <p>📍 {p.address}</p>}
              {p.contactPerson && <p>👤 {p.contactPerson}</p>}
              {p.phone && <p>📞 <a href={`tel:${p.phone}`}>{p.phone}</a></p>}
              {p.contactEmail && <p>✉️ <a href={`mailto:${p.contactEmail}`}>{p.contactEmail}</a></p>}
              {(p.mapLink || p.address) && (
                <a className="pp-btn call pp-directions" href={p.mapLink || `https://maps.google.com/?q=${encodeURIComponent(p.address)}`} target="_blank" rel="noopener noreferrer">🧭 Get Directions</a>
              )}
            </div>
            {(p.address || p.mapLink) && (
              <iframe className="pp-map" title="Location map" loading="lazy" referrerPolicy="no-referrer-when-downgrade"
                src={`https://www.google.com/maps?q=${encodeURIComponent(p.address || p.mapLink)}&output=embed`} />
            )}
          </div>
        </section>
      )}

      {/* ───── Own branded footer ───── */}
      <footer className="pp-site-footer">
        <div className="pp-foot-inner">
          <div className="pp-foot-about">
            <div className="pp-foot-brand">
              {logoSrc ? <img src={logoSrc} alt="" /> : <span className="pp-nav-logo">{initials}</span>}
              <span>{p.businessName || p.name}</span>
            </div>
            {p.bio && <p className="pp-foot-bio">{p.bio.slice(0, 160)}{p.bio.length > 160 ? "…" : ""}</p>}
          </div>
          <div className="pp-foot-contact">
            <h4>Get in touch</h4>
            {p.address && <p>📍 {p.address}</p>}
            {p.phone && <p>📞 <a href={`tel:${p.phone}`}>{p.phone}</a></p>}
            {p.contactEmail && <p>✉️ <a href={`mailto:${p.contactEmail}`}>{p.contactEmail}</a></p>}
            <div className="pp-foot-social">
              {p.socials?.instagram && <a href={p.socials.instagram} target="_blank" rel="noopener noreferrer">Instagram</a>}
              {p.socials?.facebook && <a href={p.socials.facebook} target="_blank" rel="noopener noreferrer">Facebook</a>}
              {p.socials?.website && <a href={p.socials.website} target="_blank" rel="noopener noreferrer">Website</a>}
              {waHref && <a href={waHref} target="_blank" rel="noopener noreferrer">WhatsApp</a>}
            </div>
          </div>
        </div>
        <div className="pp-foot-bottom">© {new Date().getFullYear()} {p.businessName || p.name}. All rights reserved.</div>
      </footer>

      {/* Floating WhatsApp action */}
      {waHref && <a className="pp-fab" href={waHref} target="_blank" rel="noopener noreferrer" aria-label="WhatsApp">💬</a>}

      {lightbox && (
        <div className="pp-lightbox" onClick={() => setLightbox(null)}>
          <img src={lightbox} alt="" />
          <button className="pp-lb-close">×</button>
        </div>
      )}
    </div>
  );
}
